// Conversation.js — Phase 2E.2 rewrite.
// Spec: Playbook § 5.3 (Decision 3) + § 5.2 (Decision 2) + § 6.4 (payment + verify).
// Mockup: mockups/phase_2e_2_conversation.html (9 states, Apr 2026).
//
// Two render branches:
//   - Transaction-backed thread: 240px left panel (listing context · PARTIES ·
//     5-dot stepper · state-gated SHIPPING TO · action card) + chat right.
//     Mobile (≤600px) collapses the left panel into a vertical stack.
//   - Plain chat (no transaction): single-column chat. Used by legacy threads
//     and the future "Message seller first" escape hatch.
//
// Realtime:
//   - transactions UPDATE filtered on conversation_id — wires the 48h cron flip
//     + PayPal webhook capture. Works now (Phase 2E.2 Step 1 added the table
//     to supabase_realtime).
//   - messages INSERT + conversations UPDATE — still latent until the RLS
//     hardening session lands. Code stays in place so chat goes live the
//     moment hardening ships.
//
// Counterparty + address visibility (per the Apr-2026 design call):
//   - Handle + legal name + city/state: visible from payment_pending onward
//   - Buyer's full street address: visible to seller ONLY, ONLY from
//     payment_sent onward (street address gated by payment landing)
//   - Buyer never sees seller's street address (no functional need)
//   - PII missing → italic nudge in PARTIES block, never a scarlet letter

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { supabase } from './supabaseClient';
import NavDrawer from './NavDrawer';
import { imageUrl } from './CardImage';
import { useShipmentTracking, TrackingCard } from './ShipmentTracking';
import {
  uploadChatAttachment, useAttachmentUrl, isImageType, formatBytes, MAX_ATTACHMENT_BYTES,
} from './chatAttachments';

// ─── tokens ──────────────────────────────────────────────────────────
const C = {
  pageBg: 'var(--bg)',
  cardBg: 'var(--panel-2)',
  border: 'rgba(var(--wash-rgb),0.06)',
  borderStrong: 'rgba(var(--wash-rgb),0.12)',
  text: 'var(--text-bright)',
  textDim: 'var(--muted-5)',
  textDimmer: '#5a6580',
  price: 'var(--warn)',
  paypal: '#003087',
  venmo: '#3D95CE',
  escrow: 'var(--teal)',
  green: 'var(--ok-2)',
  greenInset: 'var(--chip-ok)',
  yellow: 'var(--amber)',
  yellowInset: 'var(--chip-warn)',
  red: '#ef4444',
  purple: 'var(--violet)',
  cyan: 'var(--cyan)',
  gold: 'var(--brand-gold)',
};
const F = {
  mono: "'JetBrains Mono', monospace",
  head: "'Orbitron', monospace",
  body: "'Rajdhani', sans-serif",
  eye: "'Share Tech Mono', monospace",
};

// ─── state machine — collapse 10 statuses into 5 visible stepper buckets ───
const STEPPER_BUCKETS = [
  { key: 'pay',     label: 'PAY',     statuses: ['payment_pending'] },
  { key: 'sent',    label: 'SENT',    statuses: ['payment_sent', 'awaiting_tracking'] },
  { key: 'transit', label: 'TRANSIT', statuses: ['in_transit'] },
  { key: 'deliver', label: 'DELIVER', statuses: ['delivered'] },
  { key: 'done',    label: 'DONE',    statuses: ['confirmed_clean', 'resolved_clean'] },
];
const FLAGGED_STATUSES = ['flagged_incomplete', 'disputed', 'resolved_flagged'];
const TERMINAL_STATUSES = ['confirmed_clean', 'resolved_clean', 'resolved_flagged'];

function getStepperState(status) {
  if (FLAGGED_STATUSES.includes(status)) return { activeIndex: 4, flagged: true };
  const idx = STEPPER_BUCKETS.findIndex(b => b.statuses.includes(status));
  return { activeIndex: idx >= 0 ? idx : 0, flagged: false };
}

// ─── helpers ─────────────────────────────────────────────────────────
function navTo(path) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) +
    ' · ' + d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatPrice(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `$${Number(n).toFixed(2).replace(/\.00$/, '')}`;
}

function priceFromTxn(transaction) {
  if (!transaction || transaction.price_cents == null) return null;
  return Number(transaction.price_cents) / 100;
}

// 48h timer (Decision 3) — escalating display
//   >12h: quiet grey "42h left"
//   ≤12h: amber bold
//   ≤3h:  amber + pulse
function computeTimer(deliveredAt, now) {
  if (!deliveredAt) return null;
  const deadline = new Date(deliveredAt).getTime() + 48 * 3600 * 1000;
  const msLeft = deadline - now.getTime();
  if (msLeft <= 0) return { label: '0h left', urgency: 'expired' };
  const hours = Math.floor(msLeft / 3600000);
  const minutes = Math.floor((msLeft % 3600000) / 60000);
  const label = hours > 0
    ? (hours < 3 ? `⏱ ${hours}h ${minutes}m left` : `${hours}h left`)
    : `${minutes}m left`;
  let urgency = 'quiet';
  if (msLeft <= 3 * 3600 * 1000) urgency = 'urgent';
  else if (msLeft <= 12 * 3600 * 1000) urgency = 'warn';
  return { label, urgency };
}

// Pretty-print the system-message body to choose chip color.
function chipColorFor(body) {
  if (!body) return 'neutral';
  const b = body.toLowerCase();
  if (b.includes('declined')) return 'amber';
  if (b.includes('paypal')) return 'paypal';
  if (b.includes('venmo')) return 'venmo';
  if (b.includes('escrow')) return 'escrow';
  if (b.includes('confirmed') || b.includes('complete')) return 'green';
  if (b.includes('flag') || b.includes('expired') || b.includes('dispute')) return 'amber';
  if (b.includes('shipped') || b.includes('delivered')) return 'neutral';
  return 'neutral';
}

// ─── main component ─────────────────────────────────────────────────
export default function Conversation({ user, conversationId }) {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [transaction, setTransaction] = useState(null);
  const [otherProfile, setOtherProfile] = useState(null);
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]); // {id, file, previewUrl}
  const [sending, setSending] = useState(false);
  const [dealExpanded, setDealExpanded] = useState(false); // mobile: deal drawer open?
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(null); // tag of the in-flight action
  const [trackingInput, setTrackingInput] = useState('');
  const [carrierInput, setCarrierInput] = useState('USPS');
  const [now, setNow] = useState(() => new Date());
  const bottomRef = useRef(null);

  // Hot-clock for the 48h timer. 30s tick keeps memory + cpu cheap.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // Initial load + realtime subs
  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: conv } = await supabase.from('conversations').select('*').eq('id', conversationId).single();
      if (cancelled) return;
      if (!conv) { setLoading(false); return; }
      setConversation(conv);

      // Counterparty name + shipping address come from a participant-gated RPC:
      // profiles RLS is own-row-only, so a direct select would return nothing.
      const [{ data: oProfRows }, { data: msgs }, { data: txnRows }] = await Promise.all([
        supabase.rpc('get_conversation_counterparty', { p_conversation_id: conversationId }),
        supabase.from('messages').select('*').eq('conversation_id', conversationId).order('created_at', { ascending: true }),
        supabase.from('transactions').select('*').eq('conversation_id', conversationId).order('created_at', { ascending: false }).limit(1),
      ]);
      if (cancelled) return;
      setOtherProfile((oProfRows && oProfRows[0]) || null);
      setMessages(msgs || []);
      setTransaction((txnRows && txnRows[0]) || null);
      setLoading(false);

      // Mark read: chat rows from the other side + this user's marker for
      // system rows (which belong to both participants).
      await supabase.rpc('mark_conversation_read', { p_conversation_id: conversationId });
    })();

    // Realtime channel for this conversation
    const sub = supabase.channel('conv-' + conversationId)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${conversationId}` },
        payload => {
          setMessages(prev => prev.some(m => m.id === payload.new.id) ? prev : [...prev, payload.new]);
          // Open thread: anything arriving now is seen, including system rows
          // this user's own actions post.
          if (payload.new.sender_id !== user.id) supabase.rpc('mark_conversation_read', { p_conversation_id: conversationId });
        })
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'conversations', filter: `id=eq.${conversationId}` },
        payload => setConversation(payload.new))
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'transactions', filter: `conversation_id=eq.${conversationId}` },
        payload => setTransaction(payload.new))
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'transactions', filter: `conversation_id=eq.${conversationId}` },
        payload => setTransaction(payload.new))
      .subscribe();

    return () => { cancelled = true; supabase.removeChannel(sub); };
  }, [conversationId, user.id]);

  // Auto-scroll on new messages
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  // ─── derived ─────────────────────────────────────────────────────
  const isBuyer = transaction ? transaction.buyer_id === user.id
    : conversation?.participant_1 === user.id;
  const role = isBuyer ? 'buyer' : 'seller';
  const snap = conversation?.card_snapshot || {};
  const stepper = transaction ? getStepperState(transaction.status) : null;
  const timer = transaction?.status === 'delivered'
    ? computeTimer(transaction.delivered_at, now)
    : null;
  const { tracking, syncing: trackingSyncing, trackable } = useShipmentTracking(transaction, async () => {
    // Pull the flipped deal + the "Delivered" chat row the sync just wrote.
    const [{ data: txn }, { data: msgs }] = await Promise.all([
      supabase.from('transactions').select('*').eq('id', transaction.id).maybeSingle(),
      supabase.from('messages').select('*').eq('conversation_id', conversationId).order('created_at', { ascending: true }),
    ]);
    if (txn) setTransaction(txn);
    if (msgs) setMessages(msgs);
  });
  // Carrier is reporting scans, so delivery will be confirmed automatically.
  const trackingLive = !!(tracking?.tracker_id && !tracking.last_error);

  // ─── attachment selection ────────────────────────────────────────
  const handleAddFiles = useCallback((fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    const accepted = [];
    for (const file of picked) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        alert(`"${file.name}" is larger than 15 MB and can't be attached.`);
        continue;
      }
      accepted.push({
        id: `${Date.now()}-${Math.round(Math.random() * 1e9)}`,
        file,
        previewUrl: isImageType(file.type) ? URL.createObjectURL(file) : null,
      });
    }
    if (accepted.length) setAttachments(prev => [...prev, ...accepted]);
  }, []);

  const handleRemoveAttachment = useCallback((id) => {
    setAttachments(prev => {
      const found = prev.find(a => a.id === id);
      if (found?.previewUrl) URL.revokeObjectURL(found.previewUrl);
      return prev.filter(a => a.id !== id);
    });
  }, []);

  // ─── send chat message (text and/or attachments) ─────────────────
  const sendMessage = useCallback(async () => {
    const text = body.trim();
    if ((!text && attachments.length === 0) || sending) return;
    setSending(true);
    const pending = attachments;
    setBody('');
    setAttachments([]);

    const appendMsg = (msg) => {
      if (msg) setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
    };

    try {
      // Text first (if any), then one message per uploaded attachment.
      if (text) {
        const { data: msg } = await supabase.from('messages').insert({
          conversation_id: conversationId, sender_id: user.id, body: text,
        }).select().single();
        appendMsg(msg);
      }

      for (const att of pending) {
        try {
          const fields = await uploadChatAttachment(user.id, conversationId, att.file);
          const { data: msg } = await supabase.from('messages').insert({
            conversation_id: conversationId, sender_id: user.id, body: '', ...fields,
          }).select().single();
          appendMsg(msg);
        } catch (err) {
          console.error('[chat] attachment upload failed:', err);
          alert(`Couldn't send "${att.file.name}". Please try again.`);
        } finally {
          if (att.previewUrl) URL.revokeObjectURL(att.previewUrl);
        }
      }

      if (text || pending.length) {
        await supabase.from('conversations').update({ updated_at: new Date().toISOString() }).eq('id', conversationId);
      }
    } finally {
      setSending(false);
    }
  }, [body, attachments, sending, conversationId, user.id]);

  // ─── helper: post a system message into the thread ───────────────
  const postSystemMessage = useCallback(async (text) => {
    await supabase.from('messages').insert({
      conversation_id: conversationId, sender_id: null, body: text,
    });
    await supabase.from('conversations').update({ updated_at: new Date().toISOString() }).eq('id', conversationId);
  }, [conversationId]);

  // ─── action: buyer says they paid via Venmo (no state flip) ──────
  // Writes a system message into the thread so the seller sees a clear signal
  // to check their Venmo app. Does NOT flip transactions.status — only the
  // seller's "Mark payment received" tap is allowed to do that (prevents a
  // tapping-cargo-culted buyer from forging state).
  const handleBuyerSaysPaid = useCallback(async () => {
    if (!transaction || actionBusy) return;
    setActionBusy('buyer_says_paid');
    try {
      const sellerHandle = otherProfile?.username ? `@${otherProfile.username}` : 'Seller';
      await postSystemMessage(
        `Buyer says they sent payment via Venmo. ${sellerHandle}, confirm when you see it land in your Venmo app.`
      );
    } finally { setActionBusy(null); }
  }, [transaction, actionBusy, otherProfile, postSystemMessage]);

  // ─── action: seller marks Venmo received → flips to payment_sent ─
  const handleMarkVenmoReceived = useCallback(async () => {
    if (!transaction || actionBusy) return;
    if (transaction.payment_method !== 'venmo') return;
    if (transaction.status !== 'payment_pending') return;
    setActionBusy('mark_venmo_received');
    try {
      // Server-enforced transition (seller-only, payment_pending→payment_sent).
      // The RPC also marks the listing sold, mirroring the PayPal/Escrow webhooks.
      const { data } = await supabase.rpc('txn_mark_venmo_received', { p_transaction_id: transaction.id });
      if (data) {
        setTransaction(data);
        await postSystemMessage('Payment received via Venmo. CollectionKeeper never touches your money.');
      }
    } finally { setActionBusy(null); }
  }, [transaction, actionBusy, postSystemMessage]);

  // ─── action: seller marks shipped → flips to in_transit ──────────
  const handleMarkShipped = useCallback(async () => {
    if (!transaction || actionBusy) return;
    if (transaction.status !== 'payment_sent') return;
    if (!trackingInput.trim()) return;
    setActionBusy('mark_shipped');
    try {
      const { data } = await supabase.rpc('txn_mark_shipped', {
        p_transaction_id: transaction.id,
        p_tracking: trackingInput.trim(),
        p_carrier: carrierInput,
      });
      if (data) {
        setTransaction(data);
        await postSystemMessage(`Shipped via ${carrierInput} · ${trackingInput.trim()}`);
      }
    } finally { setActionBusy(null); }
  }, [transaction, actionBusy, trackingInput, carrierInput, postSystemMessage]);

  // ─── action: manual delivery ─────────────────────────────────────
  // Fallback only. With a recognized tracking number, the carrier's delivered
  // scan flips the deal via marketplace-tracking-webhook and this button is
  // hidden (see ActionCard in_transit).
  const handleMarkDelivered = useCallback(async () => {
    if (!transaction || actionBusy) return;
    if (transaction.status !== 'in_transit') return;
    setActionBusy('mark_delivered');
    try {
      const nowIso = new Date().toISOString();
      const { data } = await supabase.rpc('txn_mark_delivered', { p_transaction_id: transaction.id });
      if (data) {
        setTransaction(data);
        await postSystemMessage(`Delivered to buyer · ${data.carrier || 'carrier'} · ${new Date(nowIso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`);
      }
    } finally { setActionBusy(null); }
  }, [transaction, actionBusy, postSystemMessage]);

  // ─── action: buyer marks complete → flips to confirmed_clean ─────
  const handleMarkComplete = useCallback(async () => {
    if (!transaction || actionBusy) return;
    if (transaction.status !== 'delivered') return;
    if (!isBuyer) return;
    setActionBusy('mark_complete');
    try {
      // Server-enforced transition (buyer-only, delivered→confirmed_clean).
      // The RPC also retires the listing from the marketplace — done server-side
      // because the buyer can't write the seller's collections row.
      const { data } = await supabase.rpc('txn_mark_complete', { p_transaction_id: transaction.id });
      if (data) {
        setTransaction(data);
        await postSystemMessage('Both parties confirmed · Deal complete. Trust badge updated.');
      }
    } finally { setActionBusy(null); }
  }, [transaction, actionBusy, isBuyer, postSystemMessage]);

  // ─── action: request 48h more (capped at 1 per party) ────────────
  const handleRequest48 = useCallback(async () => {
    if (!transaction || actionBusy) return;
    if (transaction.status !== 'delivered') return;
    const flagField = isBuyer ? 'flag_extension_requested_buyer' : 'flag_extension_requested_seller';
    if (transaction[flagField]) return;
    setActionBusy('request_48');
    try {
      const { data } = await supabase.rpc('txn_request_extension', { p_transaction_id: transaction.id });
      if (data) {
        setTransaction(data);
        await postSystemMessage(`@${(isBuyer ? user : otherProfile)?.username || (isBuyer ? 'buyer' : 'seller')} requested 48 more hours to confirm. Extension applied.`);
      }
    } finally { setActionBusy(null); }
  }, [transaction, actionBusy, isBuyer, otherProfile, user, postSystemMessage]);

  // ─── action: open dispute (stub — Phase 2H will surface full UI) ─
  // Moves the deal to 'disputed' first so the hourly 48h job can't complete or
  // flag it while CollectionKeeper reviews, then opens the email.
  const handleOpenDispute = useCallback(async () => {
    if (transaction?.status === 'delivered' && isBuyer) {
      const { data } = await supabase.rpc('txn_report_problem', { p_transaction_id: transaction.id });
      if (data) {
        setTransaction(data);
        await postSystemMessage('The buyer reported a problem with this delivery. CollectionKeeper is reviewing the deal.');
      }
    }
    window.location.href = `mailto:keeper18@collectionkeeper.org?subject=Dispute%20on%20transaction%20${transaction?.id || ''}&body=Please%20describe%20what's%20wrong%20with%20this%20transaction%20so%20we%20can%20look%20into%20it.`;
  }, [transaction, isBuyer, postSystemMessage]);

  // ─── Marketplace 2.1: escrow-recommended routing (>=$500) ─────────
  // Escrow is the default but not mandatory at this tier. All three opt-out
  // actions run through service-role edge functions so mutual consent + audit
  // + PayPal-link generation are tamper-proof. We refetch the transaction after
  // each so the action card reflects the new routing state immediately (the
  // realtime subscription also catches it).
  const invokeEdge = useCallback(async (name, payload) => {
    const { data, error } = await supabase.functions.invoke(name, { body: payload });
    if (error) {
      let detail = null;
      try {
        if (error.context && typeof error.context.json === 'function') {
          const b = await error.context.json();
          if (b && typeof b.error === 'string') detail = b.error;
        }
      } catch (_) { /* fall through to generic */ }
      throw new Error(detail || error.message || 'Action failed.');
    }
    return data;
  }, []);

  const refetchTxn = useCallback(async () => {
    if (!conversationId) return;
    const { data } = await supabase
      .from('transactions')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (data) setTransaction(data);
  }, [conversationId]);

  const handleFundEscrow = useCallback(async () => {
    if (!transaction || actionBusy) return;
    setActionBusy('fund_escrow');
    try {
      const data = await invokeEdge('marketplace-create-escrow-deal', {
        sellerId: transaction.seller_id,
        cardKey: transaction.card_key,
      });
      if (data?.paymentLink) { try { window.open(data.paymentLink, '_blank', 'noopener'); } catch (_) {} }
      await refetchTxn();
    } catch (err) {
      console.error('[escrow routing] fund failed:', err);
      await postSystemMessage(`Couldn't open Escrow.com: ${err.message}`);
    } finally { setActionBusy(null); }
  }, [transaction, actionBusy, invokeEdge, refetchTxn, postSystemMessage]);

  // PayPal declined the last order: the webhook cleared payment_link, and
  // create-payment-link mints a fresh order on the same pending transaction.
  const handleRetryPayPal = useCallback(async () => {
    if (!transaction || actionBusy) return;
    setActionBusy('retry_paypal');
    try {
      const data = await invokeEdge('marketplace-create-payment-link', {
        sellerId: transaction.seller_id,
        cardKey: transaction.card_key,
        paymentMethod: 'paypal',
        cardSnapshot: conversation?.card_snapshot || null,
      });
      if (data?.paymentLink) { try { window.open(data.paymentLink, '_blank', 'noopener'); } catch (_) {} }
      await refetchTxn();
    } catch (err) {
      console.error('[paypal retry] failed:', err);
      alert(`Couldn't start a new PayPal payment: ${err.message}`);
    } finally { setActionBusy(null); }
  }, [transaction, actionBusy, conversation, invokeEdge, refetchTxn]);

  const handleProposeSkip = useCallback(async () => {
    if (!transaction || actionBusy) return;
    setActionBusy('propose_skip');
    try {
      await invokeEdge('marketplace-escrow-skip', { transactionId: transaction.id, action: 'propose' });
      await refetchTxn();
    } catch (err) {
      console.error('[escrow routing] propose failed:', err);
      const msg = err.message === 'seller_no_paypal'
        ? "Can't skip escrow — the seller hasn't set up a PayPal payout method."
        : `Couldn't propose skipping escrow: ${err.message}`;
      await postSystemMessage(msg);
    } finally { setActionBusy(null); }
  }, [transaction, actionBusy, invokeEdge, refetchTxn, postSystemMessage]);

  const handleConfirmSkip = useCallback(async () => {
    if (!transaction || actionBusy) return;
    setActionBusy('confirm_skip');
    try {
      const data = await invokeEdge('marketplace-escrow-skip', { transactionId: transaction.id, action: 'confirm' });
      if (data?.paymentLink) { try { window.open(data.paymentLink, '_blank', 'noopener'); } catch (_) {} }
      await refetchTxn();
    } catch (err) {
      console.error('[escrow routing] confirm failed:', err);
      await postSystemMessage(`Couldn't switch to PayPal: ${err.message}`);
    } finally { setActionBusy(null); }
  }, [transaction, actionBusy, invokeEdge, refetchTxn, postSystemMessage]);

  const handleDeclineSkip = useCallback(async () => {
    if (!transaction || actionBusy) return;
    setActionBusy('decline_skip');
    try {
      await invokeEdge('marketplace-escrow-skip', { transactionId: transaction.id, action: 'decline' });
      await refetchTxn();
    } catch (err) {
      console.error('[escrow routing] decline failed:', err);
    } finally { setActionBusy(null); }
  }, [transaction, actionBusy, invokeEdge, refetchTxn]);

  // ─── render ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={S.page}>
        <NavDrawer user={user} />
        <div style={S.empty}>Loading…</div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div style={S.page}>
        <NavDrawer user={user} />
        <div style={S.empty}>Conversation not found.</div>
      </div>
    );
  }

  const otherHandle = '@' + (otherProfile?.username || '...');

  // ─── render: plain chat (no transaction) ─────────────────────────
  if (!transaction) {
    return (
      <div style={S.page}>
        <style>{KEYFRAMES}</style>
        <NavDrawer user={user} />
        <ChatHeader otherHandle={otherHandle} otherProfile={otherProfile} snap={snap} onBack={() => navTo('/messages')} />
        <SnapBanner snap={snap} transaction={null} />
        <MessageList messages={messages} userId={user.id} bottomRef={bottomRef} />
        <ChatInput body={body} setBody={setBody} sending={sending} onSend={sendMessage}
          attachments={attachments} onAddFiles={handleAddFiles} onRemoveAttachment={handleRemoveAttachment} />
      </div>
    );
  }

  // ─── render: transaction-backed two-panel ────────────────────────
  // Shared by the desktop left-panel ActionCard and the mobile deal drawer.
  const actionProps = {
    transaction, stepper, isBuyer, otherHandle, timer, actionBusy, trackingLive,
    carrierDelivered: tracking?.status === 'delivered',
    trackingInput, setTrackingInput, carrierInput, setCarrierInput,
    onBuyerSaysPaid: handleBuyerSaysPaid,
    onMarkVenmoReceived: handleMarkVenmoReceived,
    onMarkShipped: handleMarkShipped,
    onMarkDelivered: handleMarkDelivered,
    onMarkComplete: handleMarkComplete,
    onRequest48: handleRequest48,
    onOpenDispute: handleOpenDispute,
    onFundEscrow: handleFundEscrow,
    onRetryPayPal: handleRetryPayPal,
    onProposeSkip: handleProposeSkip,
    onConfirmSkip: handleConfirmSkip,
    onDeclineSkip: handleDeclineSkip,
  };
  return (
    <div style={S.page}>
      <style>{KEYFRAMES}</style>
      <NavDrawer user={user} />
      <div className="conv-grid" style={S.convGrid}>
        {/* LEFT PANEL — listing context, parties, stepper, shipping, action */}
        <div className="conv-left-panel" style={S.leftPanel}>
          <ListingContextBlock snap={snap} transaction={transaction} />
          <PartiesBlock counterparty={otherProfile} counterpartyRole={isBuyer ? 'seller' : 'buyer'} />
          <StatusStepper stepper={stepper} />
          {/* SHIPPING TO is seller-side only, gated by payment_sent and onward */}
          {!isBuyer && transaction && otherProfile && needShippingTo(transaction.status) && (
            <ShippingToBlock counterparty={otherProfile} />
          )}
          <ActionCard {...actionProps} />
        </div>

        {/* RIGHT PANEL — chat */}
        <div style={S.rightPanel}>
          {/* Desktop: full chat header. Hidden on mobile in favor of MobileDealHeader. */}
          <div className="conv-desktop-header">
            <ChatHeader otherHandle={otherHandle} otherProfile={otherProfile} snap={snap} onBack={() => navTo('/messages')} />
          </div>
          {/* Mobile (≤600px): chat-first — deal context collapses into a tappable
              header with an always-visible progress bar; details + actions live
              in a drawer. Hidden on desktop. */}
          <MobileDealHeader
            snap={snap}
            transaction={transaction}
            stepper={stepper}
            otherProfile={otherProfile}
            otherHandle={otherHandle}
            isBuyer={isBuyer}
            expanded={dealExpanded}
            onToggle={() => setDealExpanded(v => !v)}
            onBack={() => navTo('/messages')}
            actionProps={actionProps}
          />
          {trackable && <TrackingCard transaction={transaction} tracking={tracking} syncing={trackingSyncing} />}
          <MessageList messages={messages} userId={user.id} bottomRef={bottomRef} />
          <ChatInput body={body} setBody={setBody} sending={sending} onSend={sendMessage}
          attachments={attachments} onAddFiles={handleAddFiles} onRemoveAttachment={handleRemoveAttachment} />
        </div>
      </div>
    </div>
  );
}

// ─── render gates ────────────────────────────────────────────────────
function needShippingTo(status) {
  // Buyer's full street address only visible to seller from payment_sent onward.
  return ['payment_sent', 'awaiting_tracking', 'in_transit', 'delivered',
          'confirmed_clean', 'flagged_incomplete', 'disputed',
          'resolved_clean', 'resolved_flagged'].includes(status);
}

// ═════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═════════════════════════════════════════════════════════════════════

function ChatHeader({ otherHandle, otherProfile, snap, onBack }) {
  return (
    <header style={S.header}>
      <div style={S.headerInner}>
        <button onClick={onBack} style={S.backBtn}>← MESSAGES</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={S.headerUser}>
            {otherHandle}
            {otherProfile?.identity_verified && <span style={S.verifiedPill}>✓ VERIFIED</span>}
          </div>
          {snap?.setName && (
            <div style={S.headerCard}>
              {snap.setName} · Card #{snap.cardN}
              {snap.parallelName ? ' · ' + snap.parallelName : ''}
            </div>
          )}
        </div>
      </div>
      <div style={S.headerLine} />
    </header>
  );
}

// Mobile (≤600px) chat-first header: deal context collapses into a tappable bar
// with an always-visible progress stepper; parties / shipping / action live in a
// drawer. Shown only on mobile (CSS toggles .conv-mobile-header). Reuses the same
// PartiesBlock / ShippingToBlock / ActionCard / StatusStepper as the desktop panel.
function MobileDealHeader({ snap, transaction, stepper, otherProfile, otherHandle, isBuyer, expanded, onToggle, onBack, actionProps }) {
  const [imgError, setImgError] = useState(false);
  const sellerId = transaction?.seller_id;
  const cardKey = transaction?.card_key;
  const version = snap?.photos_updated_at || snap?.has_photos || undefined;
  const imgSrc = sellerId && cardKey && !imgError ? imageUrl(sellerId, cardKey, 'front', version) : null;
  const price = priceFromTxn(transaction);
  const verified = !!otherProfile?.identity_verified;
  const showShipping = !isBuyer && transaction && otherProfile && needShippingTo(transaction.status);

  return (
    <div className="conv-mobile-header" style={MH.wrap}>
      <div style={MH.bar} onClick={onToggle}>
        <button onClick={(e) => { e.stopPropagation(); onBack(); }} style={MH.back} aria-label="Back to messages">←</button>
        <div style={MH.thumb}>
          {imgSrc ? <img src={imgSrc} alt="" style={MH.thumbImg} onError={() => setImgError(true)} /> : 'CARD'}
        </div>
        <div style={MH.mid}>
          <div style={MH.user}>
            {otherHandle}
            {verified && <span style={S.verifiedPill}>✓ VERIFIED</span>}
          </div>
          <div style={MH.sub}>
            {snap?.setName ? snap.setName + ' · ' : ''}Card #{snap?.cardN || '?'}{snap?.parallelName ? ' · ' + snap.parallelName : ''}
          </div>
        </div>
        <div style={MH.right}>
          <span style={MH.price}>{formatPrice(price)}</span>
        </div>
      </div>
      {/* always-visible progress */}
      <div onClick={onToggle} style={{ cursor: 'pointer' }}>
        <StatusStepper stepper={stepper} compact />
      </div>
      {/* expand / collapse handle — centered, clear of the top-right menu */}
      <div style={MH.grabber} onClick={onToggle}>
        <span style={{ ...MH.grabberChev, transform: expanded ? 'rotate(180deg)' : 'none' }}>⌄</span>
        {expanded ? 'HIDE DETAILS' : 'DEAL DETAILS'}
      </div>
      {/* expandable details + action */}
      <div style={{ maxHeight: expanded ? '62vh' : 0, overflowY: expanded ? 'auto' : 'hidden', transition: 'max-height .3s ease', background: C.cardBg }}>
        <div style={{ borderTop: `1px solid ${C.border}` }}>
          <PartiesBlock counterparty={otherProfile} counterpartyRole={isBuyer ? 'seller' : 'buyer'} />
          {showShipping && <ShippingToBlock counterparty={otherProfile} />}
          <ActionCard {...actionProps} />
        </div>
      </div>
      <div style={S.headerLine} />
    </div>
  );
}

function SnapBanner({ snap, transaction }) {
  if (!snap?.setName) return null;
  const price = transaction
    ? formatPrice(priceFromTxn(transaction))
    : (snap.price ? '$' + snap.price : null);
  return (
    <div style={S.snapBanner}>
      <span style={S.snapLabel}>RE: </span>
      <span style={S.snapValue}>
        {snap.setName} · Card #{snap.cardN}
        {snap.parallelName ? ' · ' + snap.parallelName : ''}
        {price ? ' · ' + price : ''}
      </span>
    </div>
  );
}

function ListingContextBlock({ snap, transaction }) {
  const price = priceFromTxn(transaction);
  // Phase 2E launch-day fix: original implementation hardcoded "CARD" as a
  // text placeholder and never wired the actual photo. Build the image URL
  // from the seller's user_id + card_key (same convention used in
  // CardImage.js across the app). Fall back to "CARD" text on load error
  // so listings without uploaded photos still render cleanly.
  const [imgError, setImgError] = useState(false);
  const sellerId = transaction?.seller_id;
  const cardKey = transaction?.card_key;
  const version = snap?.photos_updated_at || snap?.has_photos || undefined;
  const imgSrc = sellerId && cardKey && !imgError
    ? imageUrl(sellerId, cardKey, 'front', version)
    : null;

  return (
    <div style={S.listingCtx}>
      <div style={S.listingThumb}>
        {imgSrc ? (
          <img
            src={imgSrc}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 3, display: 'block' }}
            onError={() => setImgError(true)}
          />
        ) : (
          'CARD'
        )}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={S.listingSet}>{snap?.setName || '—'}</div>
        <div style={S.listingCard}>Card #{snap?.cardN || '?'}</div>
        {snap?.parallelName && (
          <span style={S.listingParallel}>{snap.parallelName}</span>
        )}
        <div style={S.listingPrice}>{formatPrice(price)}</div>
      </div>
    </div>
  );
}

function PartiesBlock({ counterparty, counterpartyRole }) {
  const cp = counterparty || {};
  const handle = cp.username ? `@${cp.username}` : '@?';
  const verified = !!cp.identity_verified;
  const first = (cp.legal_first_name || '').trim();
  const last = (cp.legal_last_name || '').trim();
  const name = (first || last) ? `${first} ${last}`.trim() : null;
  const city = (cp.shipping_city || '').trim();
  const state = (cp.shipping_state || '').trim();
  const place = city && state ? `${city}, ${state}` : (city || state || null);

  const hasName = !!name;
  const hasPlace = !!place;
  const eyebrow = counterpartyRole === 'seller' ? '// SELLER //' : '// BUYER //';

  return (
    <div style={S.parties}>
      <div style={S.eyebrow}>{eyebrow}</div>
      <div style={S.partyHandle}>
        {handle}
        {verified && <span style={S.verifiedPill}>✓ VERIFIED</span>}
      </div>
      {(hasName || hasPlace) ? (
        <div style={S.partyName}>
          {[name, place].filter(Boolean).join(' · ')}
        </div>
      ) : (
        <div style={S.partyPending}>
          Legal name + city not yet on profile. They can add it any time
          without affecting this deal.
        </div>
      )}
    </div>
  );
}

function ShippingToBlock({ counterparty }) {
  const cp = counterparty || {};
  const first = (cp.legal_first_name || '').trim();
  const last = (cp.legal_last_name || '').trim();
  const name = `${first} ${last}`.trim();
  const line1 = (cp.shipping_address_line1 || '').trim();
  const line2 = (cp.shipping_address_line2 || '').trim();
  const city = (cp.shipping_city || '').trim();
  const state = (cp.shipping_state || '').trim();
  const zip = (cp.shipping_postal_code || '').trim();
  const cityLine = [city, state].filter(Boolean).join(', ') + (zip ? ' ' + zip : '');

  const hasAny = name || line1 || cityLine.trim();

  return (
    <div style={S.shipTo}>
      <div style={S.eyebrow}>// SHIPPING TO //</div>
      {hasAny ? (
        <div style={S.shipToAddr}>
          {name && <>{name}<br /></>}
          {line1 && <>{line1}<br /></>}
          {line2 && <>{line2}<br /></>}
          {cityLine.trim() && cityLine}
        </div>
      ) : (
        <div style={S.partyPending}>
          Shipping address not on file yet. Buyer needs to add one on their profile.
        </div>
      )}
    </div>
  );
}

function StatusStepper({ stepper, compact }) {
  if (!stepper) return null;
  const { activeIndex, flagged } = stepper;
  return (
    <div style={compact ? S.stepperCompact : S.stepper}>
      {!compact && <div style={S.eyebrow}>// STATUS //</div>}
      <div style={S.stepperRow}>
        {STEPPER_BUCKETS.map((b, i) => {
          const isPast = i < activeIndex;
          const isCurrent = i === activeIndex && !flagged;
          const isFlagged = i === activeIndex && flagged;
          const dotStyle = isFlagged ? S.dotFlagged
            : isPast ? S.dotPast
            : isCurrent ? S.dotCurrent
            : S.dotFuture;
          const lineStyle = i < STEPPER_BUCKETS.length - 1
            ? (i < activeIndex ? S.lineActive : S.lineFuture)
            : null;
          const dotContent = isFlagged ? '✕' : isPast ? '✓' : isCurrent ? '●' : '○';
          return (
            <span key={b.key} style={{ display: 'flex', alignItems: 'center', flex: i === STEPPER_BUCKETS.length - 1 ? '0 0 auto' : 1 }}>
              <span style={dotStyle}>{dotContent}</span>
              {lineStyle && <span style={lineStyle} />}
            </span>
          );
        })}
      </div>
      <div style={S.stepperLabels}>
        {STEPPER_BUCKETS.map((b, i) => {
          const isPast = i < activeIndex;
          const isCurrent = i === activeIndex && !flagged;
          const isFlagged = i === activeIndex && flagged;
          const labelStyle = isFlagged ? { ...S.stepperLabel, color: C.red }
            : isPast ? { ...S.stepperLabel, color: C.green }
            : isCurrent ? { ...S.stepperLabel, color: C.yellow }
            : S.stepperLabel;
          return <span key={b.key} style={labelStyle}>{isFlagged ? 'FLAGGED' : b.label}</span>;
        })}
      </div>
    </div>
  );
}

function ActionCard(props) {
  const {
    transaction, isBuyer, otherHandle, timer, actionBusy, trackingLive, carrierDelivered,
    trackingInput, setTrackingInput, carrierInput, setCarrierInput,
    onBuyerSaysPaid, onMarkVenmoReceived, onMarkShipped, onMarkDelivered,
    onMarkComplete, onRequest48, onOpenDispute,
    onFundEscrow, onProposeSkip, onConfirmSkip, onDeclineSkip, onRetryPayPal,
  } = props;
  const status = transaction.status;
  const method = transaction.payment_method;
  const price = priceFromTxn(transaction);

  // Terminal states — single banner
  if (status === 'confirmed_clean' || status === 'resolved_clean') {
    return (
      <div style={S.action}>
        <div style={{ ...S.terminalBanner, ...S.terminalGreen }}>
          <div style={{ ...S.terminalGlyph, color: C.green }}>✓</div>
          <div style={{ ...S.terminalLabel, color: C.green }}>DEAL COMPLETE</div>
          <div style={S.terminalSub}>Trust badge updated for both parties.</div>
        </div>
      </div>
    );
  }
  if (FLAGGED_STATUSES.includes(status)) {
    const label = status === 'flagged_incomplete' ? 'AUTO-FLAGGED · 48h EXPIRED'
      : status === 'disputed' ? 'UNDER REVIEW BY @keeper18'
      : 'RESOLVED · FLAGGED';
    return (
      <div style={S.action}>
        <div style={{ ...S.terminalBanner, ...S.terminalRed }}>
          <div style={{ ...S.terminalGlyph, color: C.red }}>⚠</div>
          <div style={{ ...S.terminalLabel, color: C.red }}>{label}</div>
          <div style={S.terminalSub}>Showing on both trust histories until resolved.</div>
        </div>
        {status === 'flagged_incomplete' && (
          <button onClick={onOpenDispute} style={{ ...btnStyle('secondary'), marginTop: 14 }}>
            Email keeper18 to resolve
          </button>
        )}
      </div>
    );
  }

  // Marketplace 2.1 — escrow-recommended ROUTING for >=$500 deals. Before any
  // escrow deal is funded, the parties settle the payment rail here: escrow is
  // recommended, but either side can propose PayPal G&S and BOTH must agree.
  // Once escrow is funded (escrow_transaction_id set) this card yields to the
  // EscrowActionBranch; once a skip is agreed (payment_method flips to paypal)
  // it yields to the PayPal branch below.
  const inRouting = status === 'payment_pending'
    && !transaction.escrow_transaction_id
    && (transaction.routing_status === 'escrow_default' || transaction.routing_status === 'skip_proposed');
  if (inRouting) {
    const proposed = transaction.routing_status === 'skip_proposed';
    const iProposed = proposed
      && transaction.escrow_skip_proposed_by === (isBuyer ? transaction.buyer_id : transaction.seller_id);
    return (
      <div style={S.action}>
        {!proposed && (
          <>
            <div style={S.actionTitle}>Escrow recommended for $500+ cards</div>
            <div style={S.actionMicro}>
              Escrow.com holds the payment until delivery is confirmed — recommended for high-value cards. It's optional if both of you agree to skip it.
            </div>
            {isBuyer ? (
              <button onClick={onFundEscrow} disabled={!!actionBusy} style={btnStyle('escrow')}>
                {actionBusy === 'fund_escrow' ? 'Opening Escrow…' : `Fund with Escrow.com${price ? ` · ${formatPrice(price)}` : ''}`}
              </button>
            ) : (
              <div style={S.actionMicro}>Waiting on {otherHandle} to fund escrow.</div>
            )}
            <button onClick={onProposeSkip} disabled={!!actionBusy} style={{ ...btnStyle('secondary'), marginTop: 8 }}>
              {actionBusy === 'propose_skip' ? 'Proposing…' : 'Propose paying via PayPal G&S instead'}
            </button>
          </>
        )}
        {proposed && iProposed && (
          <>
            <div style={S.actionTitle}>Escrow-skip proposed</div>
            <div style={S.actionMicro}>
              Waiting on {otherHandle} to confirm. Until they agree, this deal stays on Escrow.com.
            </div>
            <button onClick={onDeclineSkip} disabled={!!actionBusy} style={{ ...btnStyle('secondary'), marginTop: 8 }}>
              {actionBusy === 'decline_skip' ? 'Cancelling…' : 'Cancel proposal'}
            </button>
          </>
        )}
        {proposed && !iProposed && (
          <>
            <div style={S.actionTitle}>{otherHandle} proposed skipping escrow</div>
            <div style={S.actionMicro}>
              They'd pay via PayPal Goods &amp; Services instead of Escrow.com. PayPal's buyer protection applies; Escrow.com's funds-held protection would not. Both of you must agree.
            </div>
            <button onClick={onConfirmSkip} disabled={!!actionBusy} style={btnStyle('paypal')}>
              {actionBusy === 'confirm_skip' ? 'Switching…' : 'Confirm — skip escrow, use PayPal'}
            </button>
            <button onClick={onDeclineSkip} disabled={!!actionBusy} style={{ ...btnStyle('secondary'), marginTop: 8 }}>
              {actionBusy === 'decline_skip' ? 'Keeping escrow…' : 'Keep escrow'}
            </button>
          </>
        )}
      </div>
    );
  }

  // Escrow flows — Escrow.com drives state via webhook; CK is the record-
  // keeper at this tier (Playbook § 5.2 + BUILD_GUIDE § 7). Action affordances
  // diverge from PayPal/Venmo: seller doesn't enter tracking here (does it on
  // Escrow.com), buyer doesn't mark complete here (accepts on Escrow.com).
  // We surface the current state and a deep link to the Escrow.com transaction
  // page instead.
  if (method === 'escrow') {
    return (
      <EscrowActionBranch
        status={status}
        isBuyer={isBuyer}
        otherHandle={otherHandle}
        transaction={transaction}
        timer={timer}
      />
    );
  }

  // payment_pending
  if (status === 'payment_pending') {
    if (method === 'paypal') {
      // No link on a pending PayPal deal = PayPal declined the last order.
      const declined = !transaction.payment_link;
      if (declined && isBuyer) {
        return (
          <div style={S.action}>
            <div style={S.actionTitle}>PayPal declined your payment</div>
            <div style={S.actionMicro}>No money moved. Try again, or check the payment method on your PayPal account.</div>
            <button
              onClick={onRetryPayPal}
              disabled={!!actionBusy}
              style={btnStyle('paypal')}
            >
              {actionBusy === 'retry_paypal' ? 'Opening PayPal…' : `Try again${price ? ` · ${formatPrice(price)}` : ''}`}
            </button>
          </div>
        );
      }
      if (declined) {
        return (
          <div style={S.action}>
            <div style={S.actionTitle}>PayPal declined {otherHandle}'s payment</div>
            <div style={S.actionMicro}>You haven't been paid, so don't ship yet. This updates automatically if {otherHandle} pays.</div>
          </div>
        );
      }
      if (isBuyer) {
        return (
          <div style={S.action}>
            <div style={S.actionTitle}>Complete payment to {otherHandle}</div>
            <div style={S.actionMicro}>Pay in the tab that opened, or tap here to reopen the PayPal link.</div>
            {transaction.payment_link && (
              <a href={transaction.payment_link} target="_blank" rel="noopener noreferrer" style={btnStyle('paypal')}>
                Re-open PayPal{price ? ` · ${formatPrice(price)}` : ''}
              </a>
            )}
          </div>
        );
      }
      return (
        <div style={S.action}>
          <div style={S.actionTitle}>Waiting on {otherHandle}</div>
          <div style={S.actionMicro}>PayPal will confirm payment here automatically — usually within a minute of capture.</div>
        </div>
      );
    }
    if (method === 'venmo') {
      if (isBuyer) {
        return (
          <div style={S.action}>
            <div style={S.actionTitle}>Pay {otherHandle} in the Venmo app</div>
            <div style={S.actionMicro}>Once you've sent the payment, let {otherHandle} know so they can confirm receipt.</div>
            {transaction.payment_link && (
              <a href={transaction.payment_link} target="_blank" rel="noopener noreferrer" style={btnStyle('venmo')}>
                Re-open Venmo{price ? ` · ${formatPrice(price)}` : ''}
              </a>
            )}
            <button
              onClick={onBuyerSaysPaid}
              disabled={!!actionBusy}
              style={{ ...btnStyle('secondary'), marginTop: 4 }}
            >
              {actionBusy === 'buyer_says_paid' ? 'Posting…' : `I paid · notify ${otherHandle}`}
            </button>
          </div>
        );
      }
      return (
        <div style={S.action}>
          <div style={S.actionTitle}>{otherHandle} is paying via Venmo</div>
          <div style={S.actionMicro}>
            Check your Venmo app for an incoming payment of <strong style={{ color: C.price }}>{formatPrice(price)}</strong>. When you see it land, mark it received here.
          </div>
          <button
            onClick={onMarkVenmoReceived}
            disabled={!!actionBusy}
            style={btnStyle('venmo')}
          >
            {actionBusy === 'mark_venmo_received' ? 'Confirming…' : 'Mark payment received'}
          </button>
          <button
            onClick={onOpenDispute}
            disabled={!!actionBusy}
            style={{ ...btnStyle('danger'), marginTop: 4 }}
          >
            Buyer hasn't paid · open dispute
          </button>
        </div>
      );
    }
  }

  // payment_sent / awaiting_tracking
  if (status === 'payment_sent' || status === 'awaiting_tracking') {
    if (isBuyer) {
      return (
        <div style={S.action}>
          <div style={S.actionTitle}>Payment confirmed</div>
          <div style={S.actionMicro}>Waiting on {otherHandle} to ship and enter tracking.</div>
        </div>
      );
    }
    return (
      <div style={S.action}>
        <div style={S.actionTitle}>Payment received · ready to ship</div>
        <div style={S.actionMicro}>Drop the card in the mail, then enter tracking here. Buyer is notified the moment you confirm.</div>
        <input
          type="text"
          value={trackingInput}
          onChange={(e) => setTrackingInput(e.target.value)}
          placeholder="Tracking number"
          style={S.actionInput}
        />
        <select
          value={carrierInput}
          onChange={(e) => setCarrierInput(e.target.value)}
          style={S.actionSelect}
        >
          <option>USPS</option>
          <option>UPS</option>
          <option>FedEx</option>
          <option>Other</option>
        </select>
        <button
          onClick={onMarkShipped}
          disabled={!!actionBusy || !trackingInput.trim()}
          style={btnStyle('green')}
        >
          {actionBusy === 'mark_shipped' ? 'Marking shipped…' : 'Mark as shipped'}
        </button>
      </div>
    );
  }

  // in_transit
  if (status === 'in_transit') {
    return (
      <div style={S.action}>
        <div style={S.actionTitle}>Card on the way</div>
        <div style={S.actionMicro}>
          {transaction.tracking_number
            ? `Tracking: ${transaction.carrier || 'Carrier'} · ${transaction.tracking_number}`
            : 'Tracking info pending.'}
        </div>
        {trackingLive && (
          <div style={S.actionMicro}>
            Delivery is confirmed automatically when {transaction.carrier && transaction.carrier !== 'Other' ? transaction.carrier : 'the carrier'} scans it delivered.
          </div>
        )}
        {!isBuyer && !trackingLive && (
          <button
            onClick={onMarkDelivered}
            disabled={!!actionBusy}
            style={{ ...btnStyle('secondary'), marginTop: 6 }}
          >
            {actionBusy === 'mark_delivered' ? 'Marking…' : 'Mark as delivered (manual)'}
          </button>
        )}
      </div>
    );
  }

  // delivered
  if (status === 'delivered') {
    if (isBuyer) {
      const extensionUsed = !!transaction.flag_extension_requested_buyer;
      return (
        <div style={S.action}>
          <div style={S.actionTitle}>Card delivered · confirm receipt</div>
          <div style={S.actionMicro}>
            Check the card matches the listing. Confirm to release the deal, or open a dispute if something's wrong.
            {carrierDelivered && ' No answer within 48 hours completes the deal automatically.'}
          </div>
          {timer && (
            <div style={timerStyle(timer.urgency)}>
              {timer.urgency === 'urgent' || timer.urgency === 'warn' ? '⏱ ' : ''}{timer.label}
            </div>
          )}
          <button
            onClick={onMarkComplete}
            disabled={!!actionBusy}
            style={btnStyle('green')}
          >
            {actionBusy === 'mark_complete' ? 'Confirming…' : '✓ MARK COMPLETE'}
          </button>
          <button
            onClick={onRequest48}
            disabled={!!actionBusy || extensionUsed}
            style={btnStyle('secondary')}
          >
            {extensionUsed ? 'Request 48h more (already used)' : 'Request 48h more'}
          </button>
          <button
            onClick={onOpenDispute}
            disabled={!!actionBusy}
            style={btnStyle('danger')}
          >
            Open dispute
          </button>
        </div>
      );
    }
    return (
      <div style={S.action}>
        <div style={S.actionTitle}>Waiting on {otherHandle} to confirm</div>
        <div style={S.actionMicro}>
          {carrierDelivered
            ? `${otherHandle} has 48h after delivery to report a problem. If they don't, the deal completes automatically.`
            : "Buyer has 48h after delivery to confirm receipt. If they don't, the deal auto-flags on both trust histories."}
        </div>
        {timer && (
          <div style={timerStyle(timer.urgency)}>
            {timer.urgency === 'urgent' || timer.urgency === 'warn' ? '⏱ ' : ''}{timer.label}
          </div>
        )}
      </div>
    );
  }

  // Fallback (unknown / future status)
  return (
    <div style={S.action}>
      <div style={S.actionTitle}>{status}</div>
      <div style={S.actionMicro}>Status not yet rendered. This is safe — the deal is recorded.</div>
    </div>
  );
}

// Escrow render branch — distinct from PayPal/Venmo flows because Escrow.com
// drives state for everything after the buyer funds the deal. The seller's
// mark-as-shipped, the buyer's mark-complete, the request-48h-more — all
// of those happen on Escrow.com, and `marketplace-escrow-webhook` flips
// CK's status when they do. The thread is the record-keeper + the messaging
// surface; the action affordances are "go do this on Escrow.com" links.
function EscrowActionBranch({ status, isBuyer, otherHandle, transaction, timer }) {
  const price = priceFromTxn(transaction);
  const escrowUrl = transaction.payment_link || null;

  // payment_pending — buyer needs to fund; seller waits
  if (status === 'payment_pending') {
    if (isBuyer) {
      return (
        <div style={S.action}>
          <div style={S.actionTitle}>Fund escrow with Escrow.com</div>
          <div style={S.actionMicro}>
            Escrow.com holds your payment until you confirm delivery. Both you and {otherHandle} are verified.
          </div>
          {escrowUrl && (
            <a href={escrowUrl} target="_blank" rel="noopener noreferrer" style={btnStyle('escrow')}>
              Open Escrow checkout{price ? ` · ${formatPrice(price)}` : ''}
            </a>
          )}
        </div>
      );
    }
    return (
      <div style={S.action}>
        <div style={S.actionTitle}>Waiting on {otherHandle} to fund escrow</div>
        <div style={S.actionMicro}>
          Escrow.com will notify here the moment funds clear. You can ship as soon as it does.
        </div>
        {escrowUrl && (
          <a href={escrowUrl} target="_blank" rel="noopener noreferrer" style={btnStyle('escrowSecondary')}>
            View on Escrow.com
          </a>
        )}
      </div>
    );
  }

  // payment_sent / awaiting_tracking — funds at Escrow.com; seller ships
  if (status === 'payment_sent' || status === 'awaiting_tracking') {
    if (isBuyer) {
      return (
        <div style={S.action}>
          <div style={S.actionTitle}>Escrow funded · waiting on {otherHandle} to ship</div>
          <div style={S.actionMicro}>
            The moment {otherHandle} marks the card shipped on Escrow.com, the status updates here automatically.
          </div>
          {escrowUrl && (
            <a href={escrowUrl} target="_blank" rel="noopener noreferrer" style={btnStyle('escrowSecondary')}>
              View on Escrow.com
            </a>
          )}
        </div>
      );
    }
    return (
      <div style={S.action}>
        <div style={S.actionTitle}>Funds received · ship the card</div>
        <div style={S.actionMicro}>
          Ship to the address on file, then mark shipped on Escrow.com. Insured + tracked shipping required.
        </div>
        {escrowUrl && (
          <a href={escrowUrl} target="_blank" rel="noopener noreferrer" style={btnStyle('escrow')}>
            Mark shipped on Escrow.com
          </a>
        )}
      </div>
    );
  }

  // in_transit — Escrow.com tracks; both parties view
  if (status === 'in_transit') {
    return (
      <div style={S.action}>
        <div style={S.actionTitle}>Card on the way</div>
        <div style={S.actionMicro}>
          {transaction.tracking_number
            ? `Tracking: ${transaction.carrier || 'Carrier'} · ${transaction.tracking_number}`
            : 'Tracking entered on Escrow.com.'}
        </div>
        {escrowUrl && (
          <a href={escrowUrl} target="_blank" rel="noopener noreferrer" style={btnStyle('escrowSecondary')}>
            View on Escrow.com
          </a>
        )}
      </div>
    );
  }

  // delivered — Escrow.com inspection period; buyer accepts/rejects
  if (status === 'delivered') {
    if (isBuyer) {
      return (
        <div style={S.action}>
          <div style={S.actionTitle}>Inspection period started</div>
          <div style={S.actionMicro}>
            Check the card matches the listing. Accept or reject on Escrow.com — funds release the moment you accept.
          </div>
          {timer && (
            <div style={timerStyle(timer.urgency)}>
              {timer.urgency === 'urgent' || timer.urgency === 'warn' ? '⏱ ' : ''}{timer.label}
            </div>
          )}
          {escrowUrl && (
            <a href={escrowUrl} target="_blank" rel="noopener noreferrer" style={btnStyle('escrow')}>
              Accept or reject on Escrow.com
            </a>
          )}
        </div>
      );
    }
    return (
      <div style={S.action}>
        <div style={S.actionTitle}>Waiting on {otherHandle} to inspect</div>
        <div style={S.actionMicro}>
          Buyer is in Escrow.com's inspection window. Funds release the moment they accept.
        </div>
        {timer && (
          <div style={timerStyle(timer.urgency)}>
            {timer.urgency === 'urgent' || timer.urgency === 'warn' ? '⏱ ' : ''}{timer.label}
          </div>
        )}
        {escrowUrl && (
          <a href={escrowUrl} target="_blank" rel="noopener noreferrer" style={btnStyle('escrowSecondary')}>
            View on Escrow.com
          </a>
        )}
      </div>
    );
  }

  // Fallback — any escrow status the webhook hasn't been taught yet
  return (
    <div style={S.action}>
      <div style={S.actionTitle}>{status}</div>
      <div style={S.actionMicro}>
        Escrow.com is driving this transaction. Status updates land here automatically.
      </div>
      {escrowUrl && (
        <a href={escrowUrl} target="_blank" rel="noopener noreferrer" style={btnStyle('escrowSecondary')}>
          View on Escrow.com
        </a>
      )}
    </div>
  );
}

function MessageList({ messages, userId, bottomRef }) {
  const [lightboxUrl, setLightboxUrl] = useState(null);
  return (
    <div style={S.messageArea}>
      {messages.length === 0 && <div style={S.empty}>No messages yet.</div>}
      {messages.map((msg, i) => {
        const isSystem = msg.sender_id == null;
        const isMe = msg.sender_id === userId;
        const showTime = i === 0 ||
          (new Date(msg.created_at) - new Date(messages[i - 1].created_at)) > 5 * 60 * 1000;
        if (isSystem) {
          return (
            <div key={msg.id}>
              {showTime && <div style={S.timestamp}>{formatTime(msg.created_at)}</div>}
              <div style={{ ...S.chipBase, ...chipColorStyle(chipColorFor(msg.body)) }}>{msg.body}</div>
            </div>
          );
        }
        const hasAttachment = !!msg.attachment_path;
        const hasText = !!(msg.body && msg.body.trim());
        return (
          <div key={msg.id}>
            {showTime && <div style={S.timestamp}>{formatTime(msg.created_at)}</div>}
            <div style={{ ...S.msgRow, justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
              <div style={{ ...S.bubble, ...(isMe ? S.bubbleMe : S.bubbleThem), ...(hasAttachment ? S.bubbleAttach : null) }}>
                {hasAttachment && <Attachment msg={msg} onOpenImage={setLightboxUrl} />}
                {hasText && <div style={hasAttachment ? { marginTop: 6 } : null}>{msg.body}</div>}
              </div>
            </div>
          </div>
        );
      })}
      <div ref={bottomRef} />
      {lightboxUrl && (
        <div style={S.lightbox} onClick={() => setLightboxUrl(null)}>
          <img src={lightboxUrl} alt="attachment" style={S.lightboxImg} onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

// Renders a message attachment: image thumbnail (click → lightbox) or a
// downloadable file chip. The bucket is private: URLs are signed from the
// stored path at render time and re-signed once if a load fails (expired).
function Attachment({ msg, onOpenImage }) {
  const { url, status, retry } = useAttachmentUrl(msg.attachment_path);
  const retried = useRef(false);
  const onImgError = () => {
    if (retried.current) return;
    retried.current = true;
    retry();
  };
  if (status === 'loading' && !url) {
    return <div style={S.attachPending}>Loading attachment…</div>;
  }
  if (!url) {
    return <div style={S.attachPending}>Attachment unavailable</div>;
  }
  if (isImageType(msg.attachment_type)) {
    return (
      <img
        src={url}
        alt={msg.attachment_name || 'image'}
        style={S.attachImg}
        onError={onImgError}
        onClick={() => onOpenImage(url)}
      />
    );
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" download={msg.attachment_name || true} style={S.attachFile}>
      <span style={S.attachIcon}>📎</span>
      <span style={{ minWidth: 0 }}>
        <span style={S.attachName}>{msg.attachment_name || 'Attachment'}</span>
        {msg.attachment_size ? <span style={S.attachSize}>{formatBytes(msg.attachment_size)}</span> : null}
      </span>
    </a>
  );
}

function ChatInput({ body, setBody, sending, onSend, attachments = [], onAddFiles, onRemoveAttachment }) {
  const fileRef = useRef(null);
  const canSend = (!!body.trim() || attachments.length > 0) && !sending;
  return (
    <div style={S.inputWrap}>
      {attachments.length > 0 && (
        <div style={S.tray}>
          {attachments.map(a => (
            <div key={a.id} style={S.trayItem}>
              {a.previewUrl
                ? <img src={a.previewUrl} alt={a.file.name} style={S.trayThumb} />
                : <div style={S.trayFile}><span style={{ fontSize: 16 }}>📎</span><span style={S.trayFileName}>{a.file.name}</span></div>}
              <button type="button" onClick={() => onRemoveAttachment(a.id)} style={S.trayRemove} aria-label="Remove attachment">✕</button>
            </div>
          ))}
        </div>
      )}
      <div className="conv-input-bar" style={S.inputBar}>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/*,application/pdf,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
          style={{ display: 'none' }}
          onChange={e => { onAddFiles(e.target.files); e.target.value = ''; }}
        />
        <button type="button" className="conv-attach-btn" onClick={() => fileRef.current?.click()} disabled={sending}
          style={{ ...S.attachBtn, opacity: sending ? 0.4 : 1 }} title="Attach files" aria-label="Attach files">
          📎
        </button>
        <textarea
          className="conv-input"
          style={S.input}
          value={body}
          onChange={e => setBody(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
          placeholder="Type a message… (Enter to send)"
          rows={2}
        />
        <button onClick={onSend} className="conv-send-btn" disabled={!canSend}
          style={{ ...S.sendBtn, opacity: canSend ? 1 : 0.4 }}>
          {sending ? '…' : 'SEND'}
        </button>
      </div>
    </div>
  );
}

// ─── style helpers ───────────────────────────────────────────────────
function btnStyle(variant) {
  const base = {
    display: 'block', width: '100%', border: 'none', borderRadius: 3,
    padding: '10px', fontFamily: F.mono, fontSize: 11, fontWeight: 500,
    letterSpacing: '0.04em', cursor: 'pointer', textAlign: 'center',
    marginBottom: 6, textDecoration: 'none', boxSizing: 'border-box',
  };
  if (variant === 'paypal') return { ...base, background: C.paypal, color: 'white' };
  if (variant === 'venmo')  return { ...base, background: C.venmo, color: 'white' };
  if (variant === 'escrow') return { ...base, background: C.escrow, color: 'white' };
  if (variant === 'escrowSecondary') return { ...base, background: 'transparent', color: 'var(--mint)', border: '0.5px solid rgba(var(--teal-rgb),0.4)' };
  if (variant === 'green')  return { ...base, background: 'rgba(var(--ok-2-rgb),0.14)', color: 'var(--ok)', border: '0.5px solid rgba(var(--ok-2-rgb),0.45)' };
  if (variant === 'amber')  return { ...base, background: 'rgba(var(--amber-rgb),0.12)', color: C.price, border: '0.5px solid rgba(var(--amber-rgb),0.4)' };
  if (variant === 'secondary') return { ...base, background: 'transparent', color: C.purple, border: '0.5px solid rgba(var(--violet-rgb),0.35)' };
  if (variant === 'danger') return { ...base, background: 'transparent', color: 'var(--err)', border: '0.5px solid rgba(var(--err-rgb),0.35)' };
  return base;
}

function timerStyle(urgency) {
  const base = {
    fontFamily: F.mono, fontSize: 12, margin: '0 0 10px',
    padding: '6px 8px', borderRadius: 3, textAlign: 'center',
  };
  if (urgency === 'urgent') {
    return { ...base, color: C.price, fontWeight: 600, background: 'rgba(var(--amber-rgb),0.12)', animation: 'pulseBg 1.6s ease-in-out infinite' };
  }
  if (urgency === 'warn') {
    return { ...base, color: C.price, fontWeight: 600, background: 'rgba(var(--amber-rgb),0.08)' };
  }
  return { ...base, color: C.textDim, background: 'rgba(var(--muted-5-rgb),0.08)' };
}

function chipColorStyle(kind) {
  if (kind === 'paypal')  return { background: 'rgba(0,48,135,0.32)', color: 'var(--sky)', border: '0.5px solid rgba(0,48,135,0.55)' };
  if (kind === 'venmo')   return { background: 'rgba(61,149,206,0.18)', color: 'var(--sky)', border: '0.5px solid rgba(61,149,206,0.4)' };
  if (kind === 'escrow')  return { background: 'rgba(var(--teal-rgb),0.14)', color: 'var(--mint)', border: '0.5px solid rgba(var(--teal-rgb),0.4)' };
  if (kind === 'green')   return { background: 'rgba(var(--ok-2-rgb),0.12)', color: 'var(--ok)', border: '0.5px solid rgba(var(--ok-2-rgb),0.4)' };
  if (kind === 'amber')   return { background: 'rgba(var(--amber-rgb),0.12)', color: C.price, border: '0.5px solid rgba(var(--amber-rgb),0.4)' };
  return { background: 'rgba(var(--muted-5-rgb),0.08)', color: C.textDim, border: '0.5px solid ' + C.borderStrong };
}

const KEYFRAMES = `
  @keyframes pulseRing { 0%,100% { box-shadow: 0 0 0 4px rgba(var(--amber-rgb),0.22); } 50% { box-shadow: 0 0 0 6px rgba(var(--amber-rgb),0.10); } }
  @keyframes pulseBg { 0%,100% { background: rgba(var(--amber-rgb),0.12); } 50% { background: rgba(var(--amber-rgb),0.22); } }
  .conv-mobile-header { display: none; }
  @media (max-width: 600px) {
    /* Chat-first: hide the desktop panel + header, let chat fill the screen,
       and surface the collapsible MobileDealHeader instead. */
    .conv-grid { grid-template-columns: 1fr !important; grid-template-rows: 1fr !important; }
    .conv-left-panel { display: none !important; }
    .conv-desktop-header { display: none !important; }
    .conv-mobile-header { display: block !important; }
    /* Bigger, easier-to-tap controls + safe-area + no iOS focus-zoom (16px).
       Generous side/bottom insets keep the attach + send buttons clear of the
       screen's rounded bottom corners (which otherwise clip them). */
    .conv-input-bar { padding: 10px calc(20px + env(safe-area-inset-right)) calc(18px + env(safe-area-inset-bottom)) calc(20px + env(safe-area-inset-left)) !important; gap: 10px !important; }
    .conv-attach-btn { min-width: 48px !important; min-height: 48px !important; font-size: 22px !important; display: flex !important; align-items: center !important; justify-content: center !important; padding: 0 !important; }
    .conv-send-btn { min-height: 48px !important; padding: 0 18px !important; font-size: 12px !important; }
    .conv-input { font-size: 16px !important; min-height: 48px !important; }
  }
`;

// ─── styles ──────────────────────────────────────────────────────────
const S = {
  // 100vh minus the body's native safe-area padding (theme.css) — at a plain
  // 100vh the document is taller than the viewport by the inset, so the first
  // keyboard-open scrolls the chat header up under the status bar for good.
  page: { height: 'calc(100vh - max(env(safe-area-inset-top, 0px), var(--ck-android-inset-top, 0px)))', display: 'flex', flexDirection: 'column', background: C.pageBg, color: C.text, fontFamily: F.body },
  convGrid: { display: 'grid', gridTemplateColumns: '240px 1fr', flex: 1, minHeight: 0 },
  leftPanel: { background: C.cardBg, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflowY: 'auto' },
  rightPanel: { display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 },

  // chat header (right panel top)
  header: { background: 'rgba(var(--bg-rgb),0.97)', padding: '12px 18px 0', borderBottom: `1px solid ${C.borderStrong}`, flexShrink: 0 },
  headerInner: { display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 10 },
  backBtn: { fontFamily: F.eye, fontSize: 9, letterSpacing: '1.5px', color: C.textDim, background: 'transparent', border: `1px solid ${C.borderStrong}`, padding: '5px 10px', borderRadius: 2, cursor: 'pointer', flexShrink: 0 },
  headerUser: { fontFamily: F.head, fontSize: 14, fontWeight: 600, color: 'var(--text-strong)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  headerCard: { fontFamily: F.eye, fontSize: 9, color: C.textDim, marginTop: 2, letterSpacing: '0.05em' },
  headerLine: { height: 1, background: 'linear-gradient(90deg,transparent,var(--cyan),var(--brand-gold),var(--cyan),transparent)' },
  verifiedPill: { fontFamily: F.mono, fontSize: 8, padding: '1px 5px', background: 'rgba(var(--ok-2-rgb),0.15)', color: 'var(--ok)', border: '0.5px solid rgba(var(--ok-2-rgb),0.4)', borderRadius: 2, fontWeight: 500, letterSpacing: '0.04em' },

  // listing context (left panel)
  listingCtx: { padding: 12, borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 10, alignItems: 'flex-start' },
  listingThumb: { width: 56, height: 78, background: 'var(--bg-2)', border: `1px solid ${C.border}`, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.eye, fontSize: 8, color: C.textDimmer, letterSpacing: 1, flexShrink: 0 },
  listingSet: { fontFamily: F.eye, fontSize: 9, color: C.textDim, letterSpacing: '0.05em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  listingCard: { fontFamily: F.mono, fontSize: 12, color: C.text, margin: '2px 0 4px' },
  listingParallel: { display: 'inline-block', fontFamily: F.mono, fontSize: 9, color: 'var(--peach)', border: '0.5px solid var(--sketch-orange)', padding: '1px 5px', borderRadius: 2, marginBottom: 6 },
  listingPrice: { fontFamily: F.mono, fontSize: 16, color: C.price, lineHeight: 1, marginTop: 2 },

  // parties block
  parties: { padding: '12px 14px', borderBottom: `1px solid ${C.border}` },
  eyebrow: { fontFamily: F.eye, fontSize: 9, color: C.textDim, letterSpacing: '2px', margin: '0 0 8px' },
  partyHandle: { fontFamily: F.mono, fontSize: 12, color: C.text, display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 3px', flexWrap: 'wrap' },
  partyName: { fontFamily: F.body, fontSize: 12, color: C.textDim, lineHeight: 1.4 },
  partyPending: { fontFamily: F.body, fontSize: 11, color: C.textDim, fontStyle: 'italic', lineHeight: 1.45 },

  // shipping-to block
  shipTo: { padding: '10px 14px', borderBottom: `1px solid ${C.border}`, background: 'rgba(var(--ok-2-rgb),0.04)' },
  shipToAddr: { fontFamily: F.mono, fontSize: 11, color: C.text, lineHeight: 1.55, marginTop: 2 },

  // stepper
  stepper: { padding: '14px 14px 12px', borderBottom: `1px solid ${C.border}` },
  stepperCompact: { padding: '6px 16px 10px' },
  stepperRow: { display: 'flex', alignItems: 'center', margin: '0 0 4px' },
  dotPast: { width: 14, height: 14, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 600, flexShrink: 0, background: C.green, color: C.greenInset },
  dotCurrent: { width: 14, height: 14, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 600, flexShrink: 0, background: C.yellow, color: C.yellowInset, boxShadow: '0 0 0 4px rgba(var(--amber-rgb),0.22)', animation: 'pulseRing 1.6s ease-in-out infinite' },
  dotFuture: { width: 14, height: 14, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 600, flexShrink: 0, background: 'rgba(var(--muted-5-rgb),0.18)', color: C.textDimmer },
  dotFlagged: { width: 14, height: 14, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 600, flexShrink: 0, background: C.red, color: 'var(--chip-err)' },
  lineActive: { flex: 1, height: 1, margin: '0 4px', background: 'rgba(var(--ok-2-rgb),0.55)' },
  lineFuture: { flex: 1, height: 1, margin: '0 4px', background: 'rgba(var(--muted-5-rgb),0.18)' },
  stepperLabels: { display: 'flex', justifyContent: 'space-between', marginTop: 6 },
  stepperLabel: { fontFamily: F.eye, fontSize: 8, letterSpacing: '0.05em', color: C.textDimmer, width: '18%', textAlign: 'center' },

  // action card (bottom-anchored)
  action: { marginTop: 'auto', padding: 14, borderTop: `1px solid ${C.border}` },
  actionTitle: { fontFamily: F.mono, fontSize: 11, color: C.text, margin: '0 0 8px', lineHeight: 1.4 },
  actionMicro: { fontFamily: F.body, fontSize: 11, color: C.textDim, margin: '0 0 12px', lineHeight: 1.45 },
  actionInput: { width: '100%', background: 'var(--panel-deep3)', border: `0.5px solid ${C.borderStrong}`, color: C.text, fontFamily: F.mono, fontSize: 11, padding: '8px 10px', borderRadius: 3, marginBottom: 6, outline: 'none', boxSizing: 'border-box' },
  actionSelect: { width: '100%', background: 'var(--panel-deep3)', border: `0.5px solid ${C.borderStrong}`, color: C.text, fontFamily: F.mono, fontSize: 11, padding: '8px 10px', borderRadius: 3, marginBottom: 6, boxSizing: 'border-box' },
  terminalBanner: { textAlign: 'center', padding: '18px 10px', borderRadius: 4 },
  terminalGreen: { background: 'rgba(var(--ok-2-rgb),0.08)', border: '0.5px solid rgba(var(--ok-2-rgb),0.3)' },
  terminalRed: { background: 'rgba(var(--err-rgb),0.06)', border: '0.5px solid rgba(var(--err-rgb),0.3)' },
  terminalGlyph: { fontSize: 22, marginBottom: 6 },
  terminalLabel: { fontFamily: F.mono, fontSize: 11, letterSpacing: '0.04em', marginBottom: 4 },
  terminalSub: { fontFamily: F.body, fontSize: 11, color: C.textDim },

  // chat body + bubbles + chips
  snapBanner: { background: 'rgba(var(--brand-gold-rgb),0.06)', borderBottom: '1px solid rgba(var(--brand-gold-rgb),0.15)', padding: '8px 18px', flexShrink: 0 },
  snapLabel: { fontFamily: F.head, fontSize: 8, letterSpacing: '2px', color: C.gold },
  snapValue: { fontFamily: F.eye, fontSize: 11, color: 'var(--brand-gold)' },
  messageArea: { flex: 1, overflowY: 'auto', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 8 },
  empty: { textAlign: 'center', padding: '60px 20px', fontFamily: F.eye, fontSize: 11, color: 'var(--border-2)' },
  timestamp: { textAlign: 'center', fontFamily: F.eye, fontSize: 8, color: 'var(--border-2)', margin: '8px 0 4px', letterSpacing: 1 },
  msgRow: { display: 'flex' },
  bubble: { maxWidth: '70%', padding: '9px 13px', borderRadius: 3, fontFamily: F.body, fontSize: 14, lineHeight: 1.5, wordBreak: 'break-word' },
  bubbleMe: { background: 'rgba(var(--cyan-rgb),0.12)', border: '1px solid rgba(var(--cyan-rgb),0.2)', color: C.text, borderTopRightRadius: 0 },
  bubbleThem: { background: 'rgba(var(--pcard-rgb),0.9)', border: `1px solid ${C.borderStrong}`, color: C.text, borderTopLeftRadius: 0 },
  chipBase: { alignSelf: 'center', maxWidth: '75%', padding: '8px 12px', borderRadius: 3, fontFamily: F.mono, fontSize: 11, textAlign: 'center', lineHeight: 1.5 },

  // input bar
  inputWrap: { borderTop: `1px solid ${C.borderStrong}`, flexShrink: 0, background: C.pageBg },
  inputBar: { padding: '10px 18px', display: 'flex', gap: 8, alignItems: 'flex-end' },
  input: { flex: 1, background: 'var(--panel-deep3)', border: `1px solid ${C.borderStrong}`, color: C.text, fontFamily: F.body, fontSize: 14, padding: '9px 12px', borderRadius: 2, outline: 'none', resize: 'none', minHeight: 38 },
  sendBtn: { fontFamily: F.eye, fontSize: 9, letterSpacing: '1.2px', color: C.cyan, background: 'rgba(var(--cyan-rgb),0.08)', border: '1px solid rgba(var(--cyan-rgb),0.4)', padding: '9px 16px', borderRadius: 2, cursor: 'pointer', flexShrink: 0 },
  attachBtn: { fontSize: 16, lineHeight: 1, color: C.textDim, background: 'transparent', border: `1px solid ${C.borderStrong}`, padding: '7px 10px', borderRadius: 2, cursor: 'pointer', flexShrink: 0, alignSelf: 'flex-end' },

  // attachment preview tray (above input)
  tray: { display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px 18px 0' },
  trayItem: { position: 'relative', borderRadius: 3, overflow: 'hidden' },
  trayThumb: { width: 56, height: 56, objectFit: 'cover', borderRadius: 3, border: `1px solid ${C.borderStrong}`, display: 'block' },
  trayFile: { display: 'flex', alignItems: 'center', gap: 6, maxWidth: 180, height: 56, padding: '0 10px', background: 'var(--panel-deep3)', border: `1px solid ${C.borderStrong}`, borderRadius: 3 },
  trayFileName: { fontFamily: F.mono, fontSize: 10, color: C.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  trayRemove: { position: 'absolute', top: 2, right: 2, width: 18, height: 18, lineHeight: '16px', textAlign: 'center', padding: 0, fontSize: 10, color: '#fff', background: 'rgba(0,0,0,0.65)', border: 'none', borderRadius: '50%', cursor: 'pointer' },

  // attachment bubbles
  bubbleAttach: { padding: 6 },
  attachImg: { maxWidth: 240, maxHeight: 320, borderRadius: 3, display: 'block', cursor: 'pointer' },
  attachPending: { width: 200, padding: '10px', fontFamily: F.mono, fontSize: 11, color: C.textDim, background: 'rgba(var(--wash-rgb),0.04)', border: `1px solid ${C.borderStrong}`, borderRadius: 3 },
  attachFile: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', textDecoration: 'none', background: 'rgba(var(--wash-rgb),0.04)', border: `1px solid ${C.borderStrong}`, borderRadius: 3, maxWidth: 240 },
  attachIcon: { fontSize: 18, flexShrink: 0 },
  attachName: { display: 'block', fontFamily: F.mono, fontSize: 12, color: C.cyan, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  attachSize: { display: 'block', fontFamily: F.eye, fontSize: 9, color: C.textDimmer, marginTop: 1 },
  lightbox: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000, padding: 20 },
  lightboxImg: { maxWidth: '100%', maxHeight: '90vh', borderRadius: 4, border: `1px solid ${C.borderStrong}` },
};

// ─── mobile deal-header styles (≤600px; see .conv-mobile-header) ───────
// Right padding on the bar clears NavDrawer's fixed hamburger (top:16 right:16, 40px).
const MH = {
  wrap: { flexShrink: 0, background: 'rgba(var(--bg-rgb),0.97)' },
  bar: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 60px 9px 8px', cursor: 'pointer' },
  back: { fontFamily: F.eye, fontSize: 16, lineHeight: 1, color: C.textDim, background: 'transparent', border: `1px solid ${C.borderStrong}`, borderRadius: 4, width: 34, height: 34, flexShrink: 0, cursor: 'pointer' },
  thumb: { width: 34, height: 48, borderRadius: 3, border: '1px solid rgba(var(--brand-gold-rgb),0.45)', background: 'var(--bg-3)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.eye, fontSize: 7, color: C.textDimmer, letterSpacing: 1, overflow: 'hidden' },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  mid: { flex: 1, minWidth: 0 },
  user: { fontFamily: F.head, fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  sub: { fontFamily: F.eye, fontSize: 9, color: C.textDim, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '.04em' },
  right: { display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 },
  price: { fontFamily: F.mono, fontSize: 14, color: C.price },
  grabber: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '8px 10px', cursor: 'pointer', borderTop: `1px solid ${C.border}`, fontFamily: F.eye, fontSize: 9, letterSpacing: '1.5px', color: C.textDim },
  grabberChev: { fontFamily: F.mono, fontSize: 12, color: C.textDim, transition: 'transform .25s', display: 'inline-block', lineHeight: 1 },
};
